import classNames from 'classnames';
import HotKeyTrap from 'components/HotKeyTrap';
import config from 'config';
import { AnimatePresence, motion } from 'framer-motion';
import { throttle } from 'lodash-es';
import React, { Component } from 'react';
import FontAwesome from 'react-fontawesome';
import styles from './Modal.module.scss';

interface TransformProperties {
  translateX?: string | number;
  translateY?: string | number;
  translateZ?: string | number;
}

export type ModalOrigin =
  | 'topCenterToNorth'
  | 'topRightToWest'
  | 'topRightToSouth'
  | 'topRightToEast'
  | 'topRightToNorthEast'
  | 'topLeftToSouth'
  | 'topLeftToWest'
  | 'topLeftToNorthWest'
  | 'topLeftToNorth'
  | 'bottomRightToEast'
  | 'bottomRightToSouth'
  | 'bottomLeftToSouth'
  | 'bottomLeftToWest'
  | 'center'
  | 'centerTop'
  | 'centerToWest'
  | 'bottomCenterToSouth';

interface Props {
  children: React.ReactNode;
  origin: ModalOrigin;
  autoPosition?: boolean;
  type?: string;
  animated?: boolean;
  isOpen?: boolean;
  renderWhenClosed?: boolean;
  className?: string;
  defaultHeight: number;
  defaultWidth: number;
  delay: number | Array<number>;
  onKey?: (e: KeyboardEvent) => any;
  position?: string;
  offsetY?: number;
  minOffsetY: number;
  backdrop?: 'light' | 'dark' | null;
  onClickBackdrop?: () => any;
  onClickClose?: () => any;
  showCloseIcon?: boolean;
  relativeToElementId?: string;
  hideArrow?: boolean;
  /* Specify px position for anchor (= where modal positions itself from) */
  anchorTop?: number;
  anchorLeft?: number;
  anchorRight?: number;
  anchorBottom?: number;
}

export type ModalProps = Props;

interface State {
  top: number | undefined;
  left: number | undefined;
  right: number | undefined;
  outsideTopViewport: boolean;
  outsideRightViewport: boolean;
  outsideBottomViewport: boolean;
  outsideLeftViewport: boolean;
  origin: ModalOrigin;
}

const createOriginAnimation = (
  isOpen: boolean
): Record<ModalOrigin, TransformProperties & Pick<React.CSSProperties, 'opacity'>> => ({
  center: {
    opacity: isOpen ? 1 : 0,
    translateY: isOpen ? 10 : 0,
    translateZ: 0,
    translateX: 0,
  },
  centerTop: {
    opacity: isOpen ? 1 : 0,
    translateY: isOpen ? 10 : 0,
    translateZ: 0,
    translateX: 0,
  },
  centerToWest: {
    opacity: isOpen ? 1 : 0,
    translateZ: 0,
    translateX: isOpen ? -10 : 0,
    translateY: 0,
  },
  topLeftToSouth: {
    opacity: isOpen ? 1 : 0,
    translateY: isOpen ? 0 : -10,
    translateZ: 0,
    translateX: 0,
  },
  topLeftToWest: {
    opacity: isOpen ? 1 : 0,
    translateZ: 0,
    translateX: isOpen ? -10 : 0,
    translateY: 0,
  },
  topLeftToNorthWest: {
    opacity: isOpen ? 1 : 0,
    translateZ: 0,
    translateX: isOpen ? -10 : 0,
    translateY: 0,
  },
  topLeftToNorth: {
    opacity: isOpen ? 1 : 0,
    translateZ: 0,
    translateX: isOpen ? -10 : 0,
    translateY: 0,
  },
  topRightToEast: {
    opacity: isOpen ? 1 : 0,
    translateZ: 0,
    translateX: isOpen ? 0 : -10,
    translateY: 0,
  },
  topRightToNorthEast: {
    opacity: isOpen ? 1 : 0,
    translateZ: 0,
    translateX: isOpen ? 0 : -10,
    translateY: 0,
  },
  topRightToWest: {
    opacity: isOpen ? 1 : 0,
    translateZ: 0,
    translateX: isOpen ? -10 : 0,
    translateY: 0,
  },
  topRightToSouth: {
    opacity: isOpen ? 1 : 0,
    translateY: isOpen ? 0 : -10,
    translateZ: 0,
    translateX: 0,
  },
  bottomRightToEast: {
    opacity: isOpen ? 1 : 0,
    translateZ: 0,
    translateX: isOpen ? 10 : 0,
    translateY: 0,
  },
  bottomLeftToWest: {
    opacity: isOpen ? 1 : 0,
    translateZ: 0,
    translateX: isOpen ? -10 : 0,
    translateY: 0,
  },
  bottomLeftToSouth: {
    opacity: isOpen ? 1 : 0,
    translateY: isOpen ? 0 : -10,
    translateZ: 0,
    translateX: 0,
  },
  bottomRightToSouth: {
    opacity: isOpen ? 1 : 0,
    translateY: isOpen ? 0 : -10,
    translateZ: 0,
    translateX: 0,
  },
  // If digging here to solve blurryness, the answer does not lie here:
  // Make sure the container is dividable by 2 (202px, not 203px)
  bottomCenterToSouth: {
    opacity: isOpen ? 1 : 0,
    translateY: isOpen ? 0 : -10,
    translateZ: 0,
    translateX: '-50%',
  },
  topCenterToNorth: {
    opacity: isOpen ? 1 : 0,
    translateY: isOpen ? -10 : 0,
    translateZ: 0,
    translateX: '-50%',
  },
});

const initialOrigin = createOriginAnimation(false);
const animateOrigin = createOriginAnimation(true);

class Modal extends Component<Props, State> {
  static defaultProps = {
    animated: true,
    origin: 'topRightToEast',
    // Useful for making sure animation are smooth.
    // Set placeholder dimentions if the out animation appears choppy.
    renderWhenClosed: false,
    defaultHeight: 100,
    defaultWidth: 100,
    delay: 0,
    minOffsetY: 0,
    autoPosition: true,
    showCloseIcon: false,
    backdrop: null,
  };

  ref = React.createRef<HTMLDivElement>();

  throttledPositionModal: () => void;

  constructor(props: Props) {
    super(props);

    this.throttledPositionModal = throttle(this.autoPositionModal.bind(this), 1000 / 60);

    this.state = {
      top: undefined,
      left: undefined,
      right: undefined,
      outsideTopViewport: false,
      outsideRightViewport: false,
      outsideBottomViewport: false,
      outsideLeftViewport: false,
      origin: props.origin,
    };
  }

  componentDidMount() {
    if (this.props.isOpen) {
      this.positionModal(this.props);
    }

    this.setupListeners();
  }

  componentDidUpdate(prevProps: Props) {
    const offsetChanged = prevProps.offsetY !== this.props.offsetY;
    const justOpened = !prevProps.isOpen && this.props.isOpen;
    const justClosed = prevProps.isOpen && !this.props.isOpen;
    const isFixed = this.props.position === 'fixed' || !!this.props.backdrop;
    const originChanged = prevProps.origin !== this.props.origin;

    // Determine where to position the Modal
    if ((justOpened && this.props.autoPosition) || (isFixed && offsetChanged) || originChanged) {
      this.positionModal(this.props);
    }

    // Reset auto-calculated dimentions after closing
    // so they run on next open (taking new window width into consideration)
    if (justClosed) {
      this.setState({
        origin: this.props.origin,
        outsideTopViewport: false,
        outsideRightViewport: false,
        outsideBottomViewport: false,
        outsideLeftViewport: false,
      });
    }
  }

  componentWillUnmount() {
    this.teardownListeners();
  }

  setupListeners = () => {
    if (this.props.autoPosition) {
      window.addEventListener('scroll', this.throttledPositionModal);
      window.addEventListener('resize', this.throttledPositionModal);
    }
  };

  teardownListeners = () => {
    if (this.props.autoPosition) {
      window.removeEventListener('scroll', this.throttledPositionModal);
      window.removeEventListener('resize', this.throttledPositionModal);
    }
  };

  autoPositionModal() {
    if (this.props.autoPosition) {
      this.positionModal(this.props);
    }
  }

  // @todo Expand on this as needed. It covers the basic for now.
  determineOrigin = (
    origin: ModalOrigin,
    state: {
      top: number;
      left: number;
      right: number;
      outsideTopViewport: boolean;
      outsideRightViewport: boolean;
      outsideBottomViewport: boolean;
      outsideLeftViewport: boolean;
    }
  ): ModalOrigin => {
    switch (origin) {
      case 'topRightToEast': {
        if (state.outsideLeftViewport && state.outsideRightViewport) {
          return 'center';
        }

        if (state.outsideRightViewport) {
          return 'topLeftToWest';
        }

        if (state.outsideRightViewport && state.outsideBottomViewport) {
          return 'topLeftToNorthWest';
        }

        if (state.outsideBottomViewport) {
          return 'topRightToNorthEast'; // was topRightToEast
        }

        return origin;
      }
      case 'topLeftToWest': {
        if (state.outsideBottomViewport && !state.outsideTopViewport) {
          return 'topLeftToNorthWest';
        }
        if (state.outsideBottomViewport && state.outsideTopViewport) {
          return 'bottomLeftToWest';
        }
        return origin;
      }
      case 'bottomLeftToSouth':
      case 'topLeftToSouth': {
        if (state.outsideBottomViewport && !state.outsideTopViewport) {
          return 'topLeftToNorth';
        }

        if (
          state.outsideTopViewport &&
          state.outsideRightViewport &&
          state.outsideBottomViewport &&
          state.outsideLeftViewport
        ) {
          return 'centerTop';
        }

        if (state.outsideBottomViewport && state.outsideTopViewport && !state.outsideLeftViewport) {
          return 'centerToWest';
        }
        return origin;
      }
      default:
        return origin;
    }
  };

  positionModal = (props: Props) => {
    const element = this.ref.current;

    // @todo Use container anchor size + props.origin to make outside viewport
    // calculations below take container size into consideration
    // const containerAnchor = modalPositionAnchor.parentNode;
    let containerRecs = { right: 0, top: 0, left: 0, bottom: 0 };
    if (element?.parentNode instanceof Element) {
      containerRecs = element.parentNode.getBoundingClientRect();
    }
    const { right } = containerRecs;
    let { top, left } = containerRecs;

    // WorkHistory as EventEditor attachment
    // Need to offset the WorkHistoryEntryActions dropdown by the parent's
    if (props.relativeToElementId) {
      const relativeElement = document.getElementById(props.relativeToElementId);
      if (relativeElement) {
        const relativeRects = relativeElement.getBoundingClientRect();
        top = top - relativeRects.top;
        left = left - relativeRects.left;
      }
    }

    const outsideTopViewport = top - props.defaultHeight < 0;
    const outsideRightViewport = right + props.defaultWidth > window.innerWidth;
    const outsideBottomViewport = top + props.defaultHeight > window.innerHeight;
    // the 72 extra px is the menu, to make sure that's taken into account as well
    const outsideLeftViewport = left - (props.defaultWidth + 72) < 0;

    const state = {
      top: top > props.minOffsetY ? top : props.minOffsetY,
      left,
      right,
      outsideTopViewport,
      outsideRightViewport,
      outsideBottomViewport,
      outsideLeftViewport,
    };

    const origin = this.determineOrigin(props.origin, state);

    this.setState({
      ...state,
      origin,
    });
  };

  render() {
    const {
      isOpen,
      animated,
      type,
      defaultHeight,
      defaultWidth,
      onKey,
      className,
      position,
      renderWhenClosed,
      showCloseIcon,
      anchorTop,
      anchorLeft,
      anchorRight,
      anchorBottom,
    } = this.props;
    const { origin } = this.state;
    const isFixed = position === 'fixed' || !!this.props.backdrop;

    const originClassName = origin != null ? styles[origin] : null;
    const typeClassName = type != null ? styles[type] : null;

    const containerClass = classNames(
      className,
      styles.containerBase,
      originClassName,
      typeClassName,
      {
        [styles.isAnimated]: animated,
        [styles.hideArrow]: this.props.hideArrow,
      }
    );

    const inlineStyles = ['center', 'centerTop'].includes(origin)
      ? {
          marginLeft: `-${defaultWidth / 2 - 72 / 2}px`,
          // Position at exactly the middle of screen, adjusted for animation offset of 10px
          marginTop: origin === 'center' ? -(defaultHeight / 2) - 10 : 0,
        }
      : {};

    const delay = Array.isArray(this.props.delay)
      ? isOpen
        ? this.props.delay[0]
        : this.props.delay[1]
      : this.props.delay;

    const content = (
      <div className={containerClass} data-modal style={inlineStyles}>
        {showCloseIcon ? (
          <div className={styles.close} onClick={this.props.onClickClose}>
            <FontAwesome name="times-circle" />
          </div>
        ) : null}
        {isOpen && onKey ? <HotKeyTrap onKey={onKey} /> : null}
        {isOpen || renderWhenClosed ? (
          this.props.children
        ) : (
          <div style={{ height: defaultHeight, width: defaultWidth }} />
        )}
      </div>
    );

    // Position anchor
    const anchorPositionClass = classNames(styles.modalPositionAnchor, {
      [styles.fromTopLeft]: [
        'topLeftToSouth',
        'topLeftToWest',
        'topLeftToNorthWest',
        'topLeftToNorth',
      ].includes(origin),
      [styles.fromTopRight]: [
        'topRightToWest',
        'topRightToEast',
        'topRightToNorthEast',
        'topRightToSouth',
      ].includes(origin),
      [styles.fromBottomCenter]: ['bottomCenterToSouth'].includes(origin),
      [styles.fromTopCenter]: ['topCenterToNorth'].includes(origin),
      [styles.fromBottomRight]: ['bottomRightToEast', 'bottomRightToSouth'].includes(origin),
      [styles.fromBottomLeft]: [
        'bottomLeftToWest',
        'bottomLeftToSouth',
        'bottomLeftToEast',
      ].includes(origin),
      [styles.center]: isFixed && origin === 'center',
      [styles.centerTop]: isFixed && origin === 'centerTop',
      [styles.centerToWest]: isFixed && origin === 'centerToWest',
    });

    const anchorStyle = {
      top: anchorTop,
      left: anchorLeft,
      right: anchorRight,
      bottom: anchorBottom,
    };

    // No animation
    if (!animated && isOpen && !isFixed) {
      return (
        <div className={anchorPositionClass} style={anchorStyle}>
          {content}
        </div>
      );
    }

    // Animation and fixed position:
    const fixedClass = isFixed ? styles.fixed : undefined;

    return (
      <div className={anchorPositionClass} style={anchorStyle} ref={this.ref}>
        <AnimatePresence>
          {this.props.backdrop && (
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: config.animation.duration,
              }}
              onClick={this.props.onClickBackdrop}
              className={classNames(
                styles[`backdrop-${this.props.backdrop}`],
                'ignore-react-onclickoutside'
              )}
            />
          )}
          {isOpen && (
            <motion.div
              key="content"
              className={fixedClass}
              initial={initialOrigin[origin]}
              animate={animateOrigin[origin]}
              exit={initialOrigin[origin]}
              transition={{
                duration: config.animation.duration,
                delay,
                ease: config.animation.easing,
              }}
            >
              {content}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }
}

export default Modal;
